import React, { useState, useEffect } from 'react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Clock, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Fetch available slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
    } else {
      setAvailableSlots([]);
      setSelectedTime(null);
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async () => {
    try {
      setLoading(true);
      setError('');
      
      // FIX: Use local date instead of UTC to prevent timezone shifting
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios.get(`${API}/availability/slots?date=${dateStr}`);
      setAvailableSlots(response.data.slots);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch available slots');
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime || !formData.name || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // FIX: Use local date instead of UTC to prevent timezone shifting
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const response = await axios.post(`${API}/bookings`, {
        clientName: formData.name,
        clientEmail: formData.email,
        clientPhone: formData.phone,
        bookingDate: dateStr,
        bookingTime: selectedTime
      });

      if (response.data.success) {
        setBookingConfirmed(true);
        setTimeout(() => {
          setBookingConfirmed(false);
          setSelectedDate(null);
          setSelectedTime(null);
          setFormData({ name: '', email: '', phone: '' });
        }, 5000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Calculate date range (today to 2 months ahead)
  const today = new Date();
  const twoMonthsAhead = new Date();
  twoMonthsAhead.setMonth(twoMonthsAhead.getMonth() + 2);

  return (
    <div className="booking-section" id="booking">
      <div className="container">
        <div className="section-header">
          <h2 className="heading-1">Book Your Free Consultation</h2>
          <p className="body-large">Start your transformation journey today. Schedule a free session with Vikas Vaid.</p>
        </div>

        {bookingConfirmed ? (
          <Card className="booking-confirmation">
            <CardContent className="confirmation-content">
              <CheckCircle2 className="confirmation-icon" />
              <h3 className="heading-2">Booking Confirmed!</h3>
              <p className="body-medium">Your free consultation has been scheduled. Check your email (and spam folder) for confirmation details.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="booking-grid">
            <Card className="calendar-card">
              <CardHeader>
                <CardTitle>Select a Date</CardTitle>
                <CardDescription>Choose your preferred date for the consultation (next 2 months)</CardDescription>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < today || date > twoMonthsAhead}
                  className="booking-calendar"
                />
              </CardContent>
            </Card>

            <Card className="booking-form-card">
              <CardHeader>
                <CardTitle>Complete Your Booking</CardTitle>
                <CardDescription>Fill in your details and select a time slot</CardDescription>
              </CardHeader>
              <CardContent>
                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}
                
                <form onSubmit={handleBooking} className="booking-form">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  {selectedDate && loading && (
                    <p className="loading-text">Loading available slots...</p>
                  )}

                  {selectedDate && !loading && availableSlots.length > 0 && (
                    <div className="form-group">
                      <label>Available Time Slots *</label>
                      <div className="time-slots">
                        {availableSlots.map((time) => (
                          <button
                            key={time}
                            type="button"
                            className={`time-slot ${selectedTime === time ? 'selected' : ''}`}
                            onClick={() => setSelectedTime(time)}
                          >
                            <Clock size={16} />
                            {time}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedDate && !loading && availableSlots.length === 0 && (
                    <p className="no-slots">No available slots for this date. Please select another date.</p>
                  )}

                  <Button
                    type="submit"
                    className="btn-primary"
                    disabled={!selectedDate || !selectedTime || !formData.name || !formData.email || loading}
                  >
                    {loading ? 'Processing...' : 'Confirm Free Consultation'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
